import { useRef } from 'react';
// eslint-disable-next-line lodash/import-scope
import type { DebounceSettings } from 'lodash';
import debounce from 'lodash/debounce';

import useStableInstance from './useStableInstance';

type UseDebounce = <
  TDebounceFunc extends (...args: unknown[]) => unknown,
  TDebounceSettings extends DebounceSettings,
>(
  fn: TDebounceFunc,
  timeout: number,
  options?: TDebounceSettings,
) => ((...args: Parameters<TDebounceFunc>) => void) & {
  flush: () => void;
} & (TDebounceSettings extends { leading: true }
    ? {}
    : {
        cancel: () => void;
      });

/**
 * Ensures that the reference to the function in debounce is from the most recent render.
 */
const useDebounce: UseDebounce = (fn, timeout, options) => {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const debouncedFn = useStableInstance(() =>
    debounce(
      (...args) => {
        void fnRef.current?.(...args);
      },
      timeout,
      options,
    ),
  );

  return debouncedFn;
};

export default useDebounce;
